import { useEffect, useState } from "react";
import { useLauncherContext } from "../../lib/client/contexts/LauncherContext";
import { useLauncherStore } from "../../lib/client/contexts/LauncherStoreContext";
import { getOrUpdateSession, useSessionContext } from "../lib/client/contexts/SessionContext";
import { useShallow } from "zustand/react/shallow";
import {
  useAuth,
  useDevice,
  useReset,
  useUserConfirmed,
} from "../lib/client/contexts/DeviceContext";
import type { DeviceType } from "@deskpro-product/messenger-api-schemas/messenger/database/models";
import { verifyAuth } from "../lib/client/utils/auth/verifyAuth";
import type { Session } from "@deskpro-product/messenger-api-schemas/messenger/api/conversation/mutation";
import { deepEqual } from "@tanstack/react-router";

type LoginContext = {
  hostUserJwtToken: string | null;
  messengerId: string;
  tenantId: string;
  messengerToken: string;
  device: {
    id: string;
    type: DeviceType;
  };
};

const ATTEMPTS = 3;

async function retry<K extends (...args: never[]) => Promise<Response>>(
  method: K,
  ...params: Parameters<K>
): Promise<Response> {
  let response;
  let i = 1;
  do {
    response = await method.call(null, ...params);
    if (response.status) {
      return response;
    }
    await new Promise((resolve) => setTimeout(resolve, i * 1000));
  } while (++i <= ATTEMPTS);
  return response;
}

type VerifiedResult = {
  auth: string;
  userConfirmed: Session["userConfirmed"];
  user: Session["user"];
  exp: Session["exp"];
};
async function doLogin(
  context: LoginContext,
  onError: (response: Response) => void,
): Promise<VerifiedResult | null> {
  const response = await retry(verifyAuth, context);
  if (response.ok) {
    const data = await response.json();
    if ("token" in data) {
      const session = await getOrUpdateSession({
        auth: data.token,
        ...context,
      });
      if (session?.user) {
        return {
          auth: data.token as string,
          userConfirmed: session.userConfirmed,
          user: session.user,
          exp: session.exp,
        };
      }
    }
  }
  onError(response);
  return null;
}

/**
 * This is called by the (tabs) layout, so for routes such as (eg /basePath/locale/messages, /basePath/locale/messages/[id], /basePath/locale/home, /basePath/locale/launcherAuth).
 */
export default function InitAuth({
  messengerId,
  tenantId,
  messengerToken,
  children,
}: {
  messengerId: string;
  tenantId: string;
  messengerToken: string;
  children: React.ReactNode;
}) {
  const { api } = useLauncherContext();
  const { pushDeviceToken, setPushDeviceToken, setAuthStatus } = useLauncherStore(
    useShallow((state) => ({
      pushDeviceToken: state.pushDeviceToken,
      setPushDeviceToken: state.setPushDeviceToken,
      setAuthStatus: state.setAuthStatus,
    })),
  );
  const { session, setSession, hostJwt, setHostJwt } = useSessionContext();
  const { setAuth } = useAuth();
  const { device_id, type: device_type } = useDevice();
  const { isUserConfirmed, setUserConfirmed } = useUserConfirmed();
  const reset = useReset();
  const [oldLoginContext, setOldLoginContext] = useState<LoginContext | null>(null);
  const [loop, setLoop] = useState<boolean>(false);

  useEffect(() => {
    const abortController = new AbortController();
    Promise.resolve().then(async () => {
      if (abortController.signal.aborted) {
        return;
      }
      const loginContext: LoginContext = {
        hostUserJwtToken: hostJwt,
        tenantId,
        messengerId,
        messengerToken,
        device: {
          id: device_id,
          type: device_type,
        },
      };
      if (!deepEqual(oldLoginContext, loginContext)) {
        setOldLoginContext(loginContext);
        console.log(`Logging in with host JWT "${hostJwt}" on device "${device_id}"`);
        console.trace("Logging in", loginContext);

        if (!hostJwt && isUserConfirmed) {
          reset(); // will reset device and the saved user data; which will trigger the useEffect again
        } else {
          try {
            const result = await doLogin(loginContext, (response) => {
              // Failed verify; possible invalid user for device
              if (response.status >= 400 && response.status < 500) {
                if (loop) {
                  if (!hostJwt) {
                    throw new Error(`Verify failed for guest user}`);
                  }
                  setHostJwt(null);
                }
                setLoop(true);
                reset(); // will reset device; which will trigger the useEffect again
              } else {
                throw new Error(`Verify failed due to ${response.statusText || "unknown reason"}`);
              }
            });
            if (result) {
              setLoop(false);
              setAuth(result.auth); // Set on DeviceContext
              setUserConfirmed(result.userConfirmed); // Set on DeviceContext
              setSession({ user: result.user, exp: result.exp }); // Set on SessionContext
              setAuthStatus("ok");
              console.log(`Logged in with host JWT "${hostJwt}" on device "${device_id}"`);
            }
          } catch (e) {
            setAuthStatus("error");
          }
        }
      }
    });
    return () => {
      abortController.abort();
    };
  }, [
    device_id,
    hostJwt,
    isUserConfirmed,
    messengerId,
    messengerToken,
    reset,
    setAuth,
    setAuthStatus,
    setSession,
    setUserConfirmed,
    tenantId,
    oldLoginContext,
    device_type,
    loop,
    setHostJwt,
  ]);

  // Create a device channel client when we have a session and a pushDeviceToken
  // This is used to send push notifications to the device
  useEffect(() => {
    if (!session) return;
    if (!pushDeviceToken) return;

    const init = async () => {
      await api.createDeviceChannel(device_id, {
        token: pushDeviceToken,
      });

      setPushDeviceToken(undefined);
    };
    init();
  }, [pushDeviceToken, session, setPushDeviceToken, api, device_id]);

  return session ? <>{children}</> : null;
}
